import * as React from 'react';
import { useMemo } from 'react';
import { useAtom, useSetAtom } from 'jotai';
import tw from 'twin.macro';
import { useTranslation } from 'next-i18next';
import { XMarkIcon } from '@heroicons/react/24/solid';

import { BaseModal, IconButton, Title } from '@restworld/ui-ds';

import {
  currentCompanyActionAtom,
  isCompanyForeignAtom,
  isEmployerCompanyActionModalOpenAtom,
} from '../../employer-companies.atoms';
import { ModalHeaderWrapper, ModalContentWrapper } from '../../employer-companies.styled';
import CompanyTypeChoice from './company-type-choice';
import CompanySearch from './company-search';
import CompanyDetailsForm from './company-details-form';
import { EmployerCompanyModalFooterContext } from './employer-company-modal-footer-context';

function EmployerCompanyActionModal() {
  const { t } = useTranslation('payment-and-invoices');
  const [isEmployerCompanyActionModalOpen, setIsEmployerCompanyActionModalOpen] = useAtom(
    isEmployerCompanyActionModalOpenAtom
  );
  const [currentCompanyAction, setCurrentCompanyAction] = useAtom(currentCompanyActionAtom);
  const setIsCompanyForeign = useSetAtom(isCompanyForeignAtom);
  const isChoosingCompanyType = useMemo(() => currentCompanyAction === 'chooseCompanyType', [currentCompanyAction]);
  const isSearchingCompany = useMemo(() => currentCompanyAction === 'searchCompany', [currentCompanyAction]);
  const [footer, setFooter] = React.useState<React.ReactNode>(null);

  const handleClose = React.useCallback(() => {
    setFooter(null);
    setIsEmployerCompanyActionModalOpen(false);
    // Reset state for next open
    setCurrentCompanyAction('chooseCompanyType');
    setIsCompanyForeign(false);
  }, [setIsEmployerCompanyActionModalOpen, setCurrentCompanyAction, setIsCompanyForeign]);

  const footerContextValue = React.useMemo(() => ({ setFooter }), []);

  return (
    <BaseModal
      open={isEmployerCompanyActionModalOpen}
      onClose={handleClose}
      modalStyles={[tw`px-4 md:(w-full px-0 py-0 max-w-[95%] max-h-[95%] h-[95vh]) xl:(max-w-[1200px] max-h-[650px])`]}
      noPadding
      hideHeader
      footer={footer}
      footerStyles={[tw`!py-4 !px-8`]}
    >
      <EmployerCompanyModalFooterContext.Provider value={footerContextValue}>
        <ModalHeaderWrapper>
          <Title textColor="neutral-700" fontWeight="semibold" twStyle={tw`text-lg`}>
            {currentCompanyAction === 'editCompany' ? t('edit_company') : t('new_company')}
          </Title>
          <IconButton ariaLabel={t('close')} Icon={XMarkIcon} onClick={handleClose} />
        </ModalHeaderWrapper>
        <ModalContentWrapper>
          {isChoosingCompanyType ? (
            <CompanyTypeChoice />
          ) : isSearchingCompany ? (
            <CompanySearch />
          ) : (
            <CompanyDetailsForm />
          )}
        </ModalContentWrapper>
      </EmployerCompanyModalFooterContext.Provider>
    </BaseModal>
  );
}

export default EmployerCompanyActionModal;
